import { Theme } from '@mui/material/styles/createTheme';
import { Conversation } from '@provision/entity-types';
import { getFriendlyConversationId } from '@provision/formatting-utils';
import { portalPaths } from '@provision/portal-routes';
import { ActivityMessage } from 'src/components/ActivityList/activity-list-item';
import { ActivityRefChip } from 'src/components/ActivityList/activity-ref-chip';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { ActivityFeed } from 'src/graphql/entities/activity-feed/hooks/use-activity-feed';

type ConversationData = Pick<
  Conversation,
  'conversationId' | 'partnerId' | 'subject' | 'workspaceConversationId'
>;

export const buildConversationToAddressMessage = (
  activity: ActivityFeed,
): ActivityMessage[] => {
  const actorName = activity.actor?.name?.trim() || '<Platform User>';
  const conversation = Object.fromEntries<string | number>(
    activity.changes
      .filter(({ name, value }) => name.startsWith('conversation.') && !!value)
      .map((value) => ({
        ...value,
        name: value.name.replace('conversation.', ''),
      }))
      .map(({ name, value }) => [name, value!]),
  ) as ConversationData;

  const conversationFriendlyId = getFriendlyConversationId({
    workspaceConversationId: conversation.workspaceConversationId,
    conversationId: conversation.conversationId,
    partner: { partnerId: conversation.partnerId },
  });

  if (!conversation.subject || !conversationFriendlyId) {
    return [];
  }

  switch (activity.activityType) {
    case 'createN2NRelationship': {
      return [
        {
          id: activity.id,
          icon: 'map-pin',
          iconLibrary: 'lucide',
          color: (theme: Theme) => theme.palette.custom.OV.sys.primary,
          message: (
            <>
              {actorName} associated{' '}
              <ActivityRefChip
                label={`${conversationFriendlyId} - ${conversation.subject}`}
                icon={<DynamicIcon name="message-circle" library="lucide" />}
                iconColor={(theme) => theme.palette.custom.OV.sys.tertiary}
                clickable
                path={portalPaths.conversation.full(conversationFriendlyId)}
              />{' '}
              with the site
            </>
          ),
          timestamp: activity.timestamp,
        },
      ];
    }
    case 'deleteN2NRelationship': {
      return [
        {
          id: activity.id,
          icon: 'map-pin-off',
          iconLibrary: 'lucide',
          color: (theme: Theme) => theme.palette.custom.OV.sys.error,
          message: (
            <>
              {actorName} removed{' '}
              <ActivityRefChip
                label={`${conversationFriendlyId} - ${conversation.subject}`}
                icon={<DynamicIcon name="message-circle" library="lucide" />}
                iconColor={(theme) => theme.palette.custom.OV.sys.tertiary}
                clickable
                path={portalPaths.conversation.full(conversationFriendlyId)}
              />{' '}
              from the site
            </>
          ),
          timestamp: activity.timestamp,
        },
      ];
    }
    default:
      return [];
  }
};
